import React from "react";
import { useMsal } from "@azure/msal-react";
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Body1,
  Button,
  Card,
  Caption1,
  Dropdown,
  Field,
  Input,
  Link,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option,
  RadioGroup,
  Radio,
  Spinner,
  Textarea,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import {
  ArrowLeft24Regular,
  CheckmarkCircle24Regular,
  Copy20Regular,
  Open20Regular,
  ShieldTask24Regular,
} from "@fluentui/react-icons";

import { Page } from "../components/App";
import { useRegistration } from "../context/RegistrationContext";
import { getAccessToken } from "../services/authService";
import {
  beginBackendAdminConsent,
  ADMIN_CONSENT_FAILED_MESSAGE,
  prepareSharedFolder,
  STORAGE_SETUP_FAILURE_MESSAGE,
  verifySharedFolder,
} from "../services/adminStorageService";
import {
  PrepareSharedFolderResponse,
  SharedFolderTargetType,
  VerifySharedFolderResponse,
} from "../types";
import { taskPaneBackground } from "../theme";

// A discriminated union prevents contradictory page states and keeps each
// phase's data available only where it is valid.
type PhaseState =
  | { phase: "describe" }
  | { phase: "authorize"; prepared: PrepareSharedFolderResponse }
  | { phase: "done"; verified: VerifySharedFolderResponse };

type SetupApproach = "simple" | "consent-cleanup" | "permission-evidence";

const SETUP_APPROACH_LABELS: Record<SetupApproach, string> = {
  simple: "Simple setup (default)",
  "consent-cleanup": "Setup and revert permissions",
  "permission-evidence": "Setup with permission evidence",
};

const useStyles = makeStyles({
  root: {
    height: "88vh",
    display: "flex",
    flexDirection: "column",
    ...taskPaneBackground,
    ...shorthands.padding("16px"),
    boxSizing: "border-box",
    ...shorthands.gap("12px"),
    overflow: "hidden",
  },
  headerCard: {
    display: "flex",
    flexShrink: 0,
    alignItems: "flex-start",
    ...shorthands.gap("12px"),
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding("20px"),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
  },
  headerIcon: {
    display: "flex",
    width: "40px",
    height: "40px",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    color: "#b45309",
    ...shorthands.border("1px", "solid", "#fde68a"),
    ...shorthands.borderRadius("12px"),
    backgroundColor: "#fffbeb",
  },
  eyebrow: {
    margin: 0,
    color: "#b45309",
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightBold,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
  },
  title: {
    marginTop: "2px",
    marginBottom: 0,
    color: tokens.colorNeutralForeground1,
    fontSize: "22px",
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: "1.2",
    letterSpacing: "-0.025em",
  },
  description: {
    marginTop: "6px",
    marginBottom: 0,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    lineHeight: "20px",
  },
  content: {
    display: "flex",
    minHeight: 0,
    flex: 1,
    flexDirection: "column",
    ...shorthands.gap("12px"),
    overflowY: "auto",
  },
  card: {
    display: "flex",
    flexShrink: 0,
    flexDirection: "column",
    ...shorthands.gap("16px"),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding("20px"),
    boxShadow: tokens.shadow4,
  },
  cardHeading: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("12px"),
  },
  iconBadge: {
    width: "40px",
    height: "40px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.border("1px", "solid", "#dbeafe"),
    ...shorthands.borderRadius("10px"),
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
  },
  successBadge: {
    width: "40px",
    height: "40px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.border("1px", "solid", "#a7f3d0"),
    ...shorthands.borderRadius("10px"),
    backgroundColor: "#ecfdf5",
    color: "#047857",
  },
  stepNumber: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  cardTitle: {
    margin: 0,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  cardSubtitle: {
    marginTop: "2px",
    marginBottom: 0,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  actionRow: {
    display: "flex",
    ...shorthands.gap("8px"),
    flexWrap: "wrap",
    alignItems: "center",
  },
  codeBlock: {
    fontFamily: "Consolas, Monaco, 'Courier New', monospace",
    fontSize: tokens.fontSizeBase200,
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    whiteSpace: "pre",
    overflowX: "auto",
    maxHeight: "220px",
    overflowY: "auto",
  },
  copyRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: tokens.spacingVerticalXS,
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "max-content 1fr",
    gap: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    fontSize: tokens.fontSizeBase200,
  },
  summaryLabel: {
    color: tokens.colorNeutralForeground3,
  },
  footer: {
    marginTop: "auto",
    display: "flex",
    flexShrink: 0,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalXL),
    boxShadow: tokens.shadow4,
  },
  footerButton: {
    width: "100%",
    boxShadow: "none",
  },
});

interface AdminStorageSetupPageProps {
  onNavigate: (page: Page) => void;
}

export const AdminStorageSetupPage: React.FC<AdminStorageSetupPageProps> = ({ onNavigate }) => {
  const styles = useStyles();
  const { instance } = useMsal();
  const {
    state: { roles },
  } = useRegistration();

  const hasStorageSetupAccess = roles.includes("admin");
  if (!hasStorageSetupAccess) {
    return <NoAccessCard styles={styles} onBack={() => onNavigate("taskSelection")} />;
  }

  return (
    <div className={styles.root}>
      <PageHeading styles={styles} />
      <div className={styles.content}>
        <ConsentStep styles={styles} />
        <RegistrationFlow styles={styles} instance={instance} />
      </div>
      <Card className={styles.footer}>
        <Button
          appearance="primary"
          className={mergeClasses(styles.footerButton, "sourcestone-navigation-primary")}
          icon={<ArrowLeft24Regular />}
          onClick={() => onNavigate("taskSelection")}
        >
          Back to tasks
        </Button>
      </Card>
    </div>
  );
};

const PageHeading: React.FC<{ styles: ReturnType<typeof useStyles> }> = ({ styles }) => (
  <Card className={styles.headerCard}>
    <div className={styles.headerIcon} aria-hidden>
      <ShieldTask24Regular />
    </div>
    <div>
      <p className={styles.eyebrow}>Admin setup</p>
      <h1 className={styles.title}>Set up SharePoint sources</h1>
      <p className={styles.description}>
        Approve Sourcestone&apos;s storage permissions, then register the SharePoint sites and
        shared folders your tenant can use.
      </p>
    </div>
  </Card>
);

const ConsentStep: React.FC<{ styles: ReturnType<typeof useStyles> }> = ({ styles }) => {
  const [isConsenting, setIsConsenting] = React.useState(false);
  const [consentMessage, setConsentMessage] = React.useState<{
    text: string;
    intent: "info" | "error";
  } | null>(null);

  const handleConsent = async () => {
    setConsentMessage(null);
    setIsConsenting(true);
    try {
      await beginBackendAdminConsent();
      setConsentMessage({
        text: "If you approved the Microsoft 365 prompt, you're ready to register shared locations below.",
        intent: "info",
      });
    } catch (error) {
      setConsentMessage({
        text: error instanceof Error ? error.message : ADMIN_CONSENT_FAILED_MESSAGE,
        intent: "error",
      });
    } finally {
      setIsConsenting(false);
    }
  };

  return (
    <Card className={styles.card}>
      <div className={styles.cardHeading}>
        <div className={styles.iconBadge} aria-hidden>
          <span className={styles.stepNumber}>1</span>
        </div>
        <div>
          <h2 className={styles.cardTitle}>Approve storage access</h2>
          <p className={styles.cardSubtitle}>One-time setup for your tenant</p>
        </div>
      </div>
      <Body1>
        This opens a Microsoft admin-consent prompt for Sourcestone. It grants Sourcestone only the
        narrow delegated scopes it needs to see shared locations you explicitly register below
        &mdash; not broad access to your tenant&apos;s storage.
      </Body1>
      <div className={styles.actionRow}>
        <Button appearance="primary" size="large" onClick={handleConsent} disabled={isConsenting}>
          Approve storage access
        </Button>
        {isConsenting && <Spinner label="Opening consent prompt…" />}
      </div>
      {consentMessage && (
        <MessageBar
          intent={consentMessage.intent}
          role={consentMessage.intent === "error" ? "alert" : "status"}
        >
          <MessageBarTitle>Consent step</MessageBarTitle>
          <MessageBarBody>{consentMessage.text}</MessageBarBody>
        </MessageBar>
      )}
    </Card>
  );
};

type MsalLikeInstance = ReturnType<typeof useMsal>["instance"];

const RegistrationFlow: React.FC<{
  styles: ReturnType<typeof useStyles>;
  instance: MsalLikeInstance;
}> = ({ styles, instance }) => {
  const [state, setState] = React.useState<PhaseState>({ phase: "describe" });

  const resetToDescribe = () => setState({ phase: "describe" });
  const advanceToAuthorize = (prepared: PrepareSharedFolderResponse) =>
    setState({ phase: "authorize", prepared });
  const advanceToDone = (verified: VerifySharedFolderResponse) =>
    setState({ phase: "done", verified });

  return (
    <>
      <DescribeStep
        styles={styles}
        instance={instance}
        state={state}
        onPrepared={advanceToAuthorize}
      />
      {state.phase === "authorize" && (
        <AuthorizeStep
          styles={styles}
          instance={instance}
          prepared={state.prepared}
          onVerified={advanceToDone}
          onStartOver={resetToDescribe}
        />
      )}
      {state.phase === "done" && (
        <DoneStep styles={styles} verified={state.verified} onRegisterAnother={resetToDescribe} />
      )}
    </>
  );
};

const DescribeStep: React.FC<{
  styles: ReturnType<typeof useStyles>;
  instance: MsalLikeInstance;
  state: PhaseState;
  onPrepared: (prepared: PrepareSharedFolderResponse) => void;
}> = ({ styles, instance, state, onPrepared }) => {
  const [folderUrl, setFolderUrl] = React.useState("");
  const [targetType, setTargetType] = React.useState<SharedFolderTargetType>("folder");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const isLocked = state.phase !== "describe";

  const handleContinue = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      const accessToken = await getAccessToken(instance);
      const prepared = await prepareSharedFolder(folderUrl.trim(), accessToken, targetType);
      onPrepared(prepared);
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : STORAGE_SETUP_FAILURE_MESSAGE;
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className={styles.card}>
      <div className={styles.cardHeading}>
        <div className={styles.iconBadge} aria-hidden>
          <span className={styles.stepNumber}>2</span>
        </div>
        <div>
          <h2 className={styles.cardTitle}>Describe the shared location</h2>
          <p className={styles.cardSubtitle}>Choose the exact folder or SharePoint site</p>
        </div>
      </div>
      <Body1>
        Paste the SharePoint or OneDrive URL you want to register, then choose whether to give
        Sourcestone access to the whole site or just a specific folder within it.
      </Body1>
      <Field label="Access level">
        <RadioGroup
          value={targetType}
          onChange={(_, data) => setTargetType(data.value as SharedFolderTargetType)}
          layout="horizontal"
          disabled={isLocked}
        >
          <Radio value="folder" label="Specific folder (recommended)" />
          <Radio value="site" label="Full SharePoint site" />
        </RadioGroup>
      </Field>
      <Field
        label={targetType === "folder" ? "Folder URL" : "Site URL"}
        hint={
          targetType === "folder"
            ? "Select the folder in SharePoint or OneDrive, choose Copy link, and paste the complete link here."
            : "Example: https://contoso.sharepoint.com/sites/ProjectX"
        }
      >
        <Input
          value={folderUrl}
          onChange={(_, data) => setFolderUrl(data.value)}
          placeholder="https://contoso.sharepoint.com/..."
          disabled={isLocked}
        />
      </Field>
      <div className={styles.actionRow}>
        <Button
          appearance="primary"
          size="large"
          disabled={isSubmitting || isLocked || !folderUrl.trim()}
          onClick={handleContinue}
        >
          Continue
        </Button>
        {isSubmitting && <Spinner label="Preparing setup…" />}
        {isLocked && (
          <Caption1>Location locked while you complete the authorisation step below.</Caption1>
        )}
      </div>
      {error && (
        <MessageBar intent="error">
          <MessageBarTitle>Location setup failed</MessageBarTitle>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
    </Card>
  );
};

const AuthorizeStep: React.FC<{
  styles: ReturnType<typeof useStyles>;
  instance: MsalLikeInstance;
  prepared: PrepareSharedFolderResponse;
  onVerified: (verified: VerifySharedFolderResponse) => void;
  onStartOver: () => void;
}> = ({ styles, instance, prepared, onVerified, onStartOver }) => {
  const [setupApproach, setSetupApproach] = React.useState<SetupApproach>("simple");
  const [isVerifying, setIsVerifying] = React.useState(false);
  const [verifyError, setVerifyError] = React.useState<string | null>(null);
  const [copyFeedback, setCopyFeedback] = React.useState<string | null>(null);
  const scriptsByApproach: Record<SetupApproach, string> = {
    simple: prepared.powershell_snippet,
    "consent-cleanup": prepared.powershell_snippet_with_consent_cleanup,
    "permission-evidence": prepared.powershell_snippet_with_permission_evidence,
  };
  const selectedScript = scriptsByApproach[setupApproach];
  const isSite = prepared.target_type === "site";

  const handleOpenGraphExplorer = () => {
    if (prepared.graph_explorer_url) {
      window.open(prepared.graph_explorer_url, "_blank", "noopener,noreferrer");
    }
  };

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback(`${label} copied to clipboard.`);
    } catch {
      setCopyFeedback(`Select the text in the box and copy manually (${label}).`);
    }
  };

  const handleVerify = async () => {
    setVerifyError(null);
    setIsVerifying(true);
    try {
      const accessToken = await getAccessToken(instance);
      if (!prepared.registration_id) {
        throw new Error("Please start over to prepare this location again.");
      }
      const verified = await verifySharedFolder(prepared.registration_id, accessToken);
      onVerified(verified);
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : STORAGE_SETUP_FAILURE_MESSAGE;
      setVerifyError(message);
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Card className={styles.card}>
      <div className={styles.cardHeading}>
        <div className={styles.iconBadge} aria-hidden>
          <span className={styles.stepNumber}>3</span>
        </div>
        <div>
          <h2 className={styles.cardTitle}>Authorise Sourcestone</h2>
          <p className={styles.cardSubtitle}>Grant access only to this location</p>
        </div>
      </div>
      <Body1>
        This authorisation covers {isSite ? "the whole SharePoint site" : "the selected folder"}{" "}
        <strong>{prepared.display_name}</strong>
        {isSite ? "." : " and its contents."} Sourcestone can read and write only where the
        signed-in user also has permission.
      </Body1>
      <Field
        label="Setup approach"
        hint="Simple setup is the default. The other options can restore temporary setup consent or add permission evidence."
      >
        <Dropdown
          aria-label="Setup approach"
          value={SETUP_APPROACH_LABELS[setupApproach]}
          selectedOptions={[setupApproach]}
          onOptionSelect={(_, data) => setSetupApproach(data.optionValue as SetupApproach)}
        >
          <Option value="simple">Simple setup (default)</Option>
          <Option value="consent-cleanup">Setup and revert permissions</Option>
          <Option value="permission-evidence">Setup with permission evidence</Option>
        </Dropdown>
      </Field>
      <ol data-testid="cloud-shell-instructions">
        <li>Copy the snippet below</li>
        <li>
          Click <em>Open Azure Cloud Shell</em> (choose <em>PowerShell</em> if prompted)
        </li>
        <li>
          Paste the snippet and press Enter to give Sourcestone the appropriate delegated
          permissions
        </li>
        <li>
          Sign in with your <strong>Sharepoint tenant admin</strong> account
        </li>
        <li>
          Return here and click <em>Verify access</em> to confirm success.
        </li>
      </ol>

      <pre className={styles.codeBlock}>{selectedScript}</pre>
      <div className={styles.actionRow}>
        <Button
          appearance="primary"
          icon={<Copy20Regular />}
          onClick={() => handleCopy(selectedScript, "PowerShell snippet")}
        >
          Copy PowerShell snippet
        </Button>
        <Button
          appearance="secondary"
          icon={<Open20Regular />}
          onClick={() => window.open("https://shell.azure.com", "_blank", "noopener,noreferrer")}
        >
          Open Azure Cloud Shell
        </Button>
      </div>

      {prepared.web_url && (
        <div className={styles.actionRow}>
          <Link href={prepared.web_url} target="_blank" rel="noopener noreferrer">
            Open {prepared.target_type === "site" ? "site" : "folder"} in SharePoint
          </Link>
        </div>
      )}

      {prepared.graph_explorer_url && (
        <Accordion collapsible>
          <AccordionItem value="ge">
            <AccordionHeader>Prefer Microsoft Graph Explorer instead?</AccordionHeader>
            <AccordionPanel>
              <MessageBar intent="warning">
                <MessageBarBody>
                  Graph Explorer needs two prerequisites that PowerShell handles for you: you must
                  be a <strong>SharePoint (or Global) administrator</strong>, and you must first
                  consent to <code>Sites.FullControl.All</code> on Graph Explorer&apos;s{" "}
                  <em>Modify permissions</em> tab. Skipping the consent is what produces a{" "}
                  <code>403 Forbidden</code>.
                </MessageBarBody>
              </MessageBar>
              <Body1
                style={{
                  marginTop: tokens.spacingVerticalS,
                  marginBottom: tokens.spacingVerticalS,
                }}
              >
                Then: sign in, the <code>POST</code> URL is pre-filled, open the{" "}
                <em>Request body</em> tab, paste the JSON below, and click <em>Run query</em>. A 201
                response means the grant landed.
              </Body1>
              <div className={styles.actionRow}>
                <Button
                  appearance="secondary"
                  icon={<Open20Regular />}
                  onClick={handleOpenGraphExplorer}
                >
                  Open Microsoft Graph Explorer
                </Button>
              </div>
              <Body1 style={{ marginTop: tokens.spacingVerticalM }}>
                <strong>Request body to paste</strong> (target URL:{" "}
                <code>{prepared.graph_api_url}</code>)
              </Body1>
              <Textarea
                value={JSON.stringify(prepared.graph_api_body, null, 2)}
                readOnly
                rows={8}
                resize="vertical"
              />
              <div className={styles.copyRow}>
                <Button
                  appearance="secondary"
                  icon={<Copy20Regular />}
                  onClick={() =>
                    handleCopy(JSON.stringify(prepared.graph_api_body, null, 2), "Request body")
                  }
                >
                  Copy JSON body
                </Button>
              </div>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}

      {copyFeedback && (
        <MessageBar intent="info">
          <MessageBarBody>{copyFeedback}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.actionRow}>
        <Button
          appearance="primary"
          size="large"
          icon={<CheckmarkCircle24Regular />}
          onClick={handleVerify}
          disabled={isVerifying}
        >
          I&apos;ve completed the authorisation &mdash; Verify access
        </Button>
        <Button appearance="subtle" onClick={onStartOver} disabled={isVerifying}>
          Start over with a different location
        </Button>
        {isVerifying && <Spinner label="Checking access…" />}
      </div>
      {verifyError && (
        <MessageBar intent="error">
          <MessageBarTitle>Verification incomplete</MessageBarTitle>
          <MessageBarBody>{verifyError}</MessageBarBody>
        </MessageBar>
      )}
    </Card>
  );
};

const DoneStep: React.FC<{
  styles: ReturnType<typeof useStyles>;
  verified: VerifySharedFolderResponse;
  onRegisterAnother: () => void;
}> = ({ styles, verified, onRegisterAnother }) => (
  <Card className={styles.card}>
    <div className={styles.cardHeading}>
      <div className={styles.successBadge} aria-hidden>
        <CheckmarkCircle24Regular fontSize={24} />
      </div>
      <div>
        <h2 className={styles.cardTitle}>Location registered</h2>
        <p className={styles.cardSubtitle}>Ready for authorised tenant users</p>
      </div>
    </div>
    <Body1>
      <strong>{verified.display_name}</strong> is now available to users in your tenant.
    </Body1>
    <div className={styles.summaryGrid}>
      <span className={styles.summaryLabel}>Type</span>
      <span>{verified.target_type === "site" ? "SharePoint site" : "Shared folder"}</span>
      <span className={styles.summaryLabel}>Status</span>
      <span>{verified.status}</span>
      {verified.web_url && (
        <>
          <span className={styles.summaryLabel}>Link</span>
          <Link href={verified.web_url} target="_blank" rel="noopener noreferrer">
            Open in SharePoint
          </Link>
        </>
      )}
    </div>
    <div className={styles.actionRow}>
      <Button appearance="primary" size="large" onClick={onRegisterAnother}>
        Register another location
      </Button>
    </div>
  </Card>
);

const NoAccessCard: React.FC<{ styles: ReturnType<typeof useStyles>; onBack: () => void }> = ({
  styles,
  onBack,
}) => (
  <div className={styles.root}>
    <Card className={styles.headerCard}>
      <div className={styles.headerIcon} aria-hidden>
        <ShieldTask24Regular />
      </div>
      <div>
        <p className={styles.eyebrow}>Admin setup</p>
        <h1 className={styles.title}>Access required</h1>
      </div>
    </Card>
    <Card className={styles.card}>
      <Body1>
        This page is available only to users with the <code>admin</code> role.
      </Body1>
    </Card>
    <Card className={styles.footer}>
      <Button
        appearance="primary"
        className={mergeClasses(styles.footerButton, "sourcestone-navigation-primary")}
        icon={<ArrowLeft24Regular />}
        onClick={onBack}
      >
        Back to tasks
      </Button>
    </Card>
  </div>
);
